import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { date, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { HttpStatus } from 'config/Api.Config';
import { first, flatten, groupBy, isEmpty, set, sortBy } from 'lodash';
import claimFindApi from 'models/claims/claim/find/Api.Claim.Find';
import claimSaveSimpleApi from 'models/claims/claim/saveSimple/Api.Claim.SaveSimple';
import claimDocumentSaveApi from 'models/claims/claimDocument/save/Api.ClaimDocument.Save';
import claimJobDocumentSaveApi from 'models/claims/claimJobDocument/save/Api.ClaimJobDocument.Save';
import ClaimJobItemPayee from 'models/claims/claimJobItemPayee/Model.ClaimJobItemPayee';
import ClaimType from 'models/claims/claimType/Model.ClaimType';
import Payee from 'models/claims/payee/Model.Payee';
import PayeeType from 'models/claims/payeeType/Model.PayeeType';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import { ClaimCreatorOptions } from 'models/enums/ClaimCreation';
import ClaimJobStatusCode from 'models/enums/ClaimJobStatusCode';
import ClaimStatusCode from 'models/enums/ClaimStatusCode';
import ClaimTypeDataPointInputType from 'models/enums/ClaimTypeDataPointInputType';
import Currency from 'models/enums/Currency';
import LineItem from 'models/enums/LineItem';
import {
  filterLossCodesBySection,
  filterOutDisabledLossCodes,
  filterOutInternalLossCodes,
} from 'models/enums/LossCodeSection';
import PaymentStatus from 'models/enums/PaymentStatus';
import ProductType from 'models/enums/ProductType';
import Insurer from 'models/productManagement/insurer/Model.Insurer';
import importLegacyContractApi from 'models/sales/contract/legacy/importLegacyContract/Api.ImportLegacyContract';
import { createClaimTypeDataPointVisibility } from 'module/automation/schemas/ClaimAutomationSchema';
import { mapLegacyContractStatusForImport } from 'module/claims/helper/Claim.ContractEligibility';
import { getVisibleDataPoints } from 'module/claims/views/claim/other/Claim.Job.DataPoints.Inputs';
import { useEffect, useMemo, useState } from 'react';

export type SimpleClaimForm = Api.Core.Claim.SaveSimple.IRequest & {
  uploadableClaimTypeDocuments: Model.IClaimTypeDocument[];
};
export type SimpleClaimJobItem = Utils.FormData<
  Utils.Unpacked<Api.Core.Claim.SaveSimple.ClaimJobForUpdate['claimJobItems']>
>;
export default function useClaimSimpleClaim(props: {
  contract: Model.IContract;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { lang } = getLocales();
  const { user, userId, isDealer } = useAuthStore();

  const form = useForm<SimpleClaimForm>({
    default: {
      submitedDateUtc: date().tz('UTC').format(defaultServerDateTimeFormat),
      createdAtUtc: date().tz('UTC').format(defaultServerDateTimeFormat),
      modifiedAtUtc: date().tz('UTC').format(defaultServerDateTimeFormat),
      statusCode: ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_SELLING_DEALER,
      contract: props.contract,
      contractId: props.contract?.id,
      ownerId: null,
      reporterId: userId,
      claimDocuments: [],
      claimJobs: [
        {
          jobNumber: 1,
          claimTypeId: undefined,
          cause: undefined,
          customerComplaint: undefined,
          correction: undefined,
          ownerId: userId,
          statusCode: ClaimJobStatusCode.OPEN_ACTIVE_IN_PROGRESS,
          dataPoints: [],
        },
      ],
    },
    onSubmit: async (data) => {
      if (!props.contract.id) {
        const contractForImport = mapLegacyContractStatusForImport(props.contract);
        const res = await importLegacyContractApi(contractForImport);
        if (res.status !== HttpStatus.ok || !res?.payload?.id) {
          addLog({ error: res.message || lang.saveError });
          return;
        }

        data = {
          ...data,
          contractId: res.payload.id,
          contract: { ...contractForImport, id: res.payload.id },
        };
      }

      const finalClaimJobs = data?.claimJobs?.map((it, idx) => ({
        ...it,
        ownerId: userId,
        jobNumber: idx + 1,
        statusCode: ClaimJobStatusCode.OPEN_ACTIVE_IN_PROGRESS,
        claimJobItems: it?.claimJobItems?.length
          ? it?.claimJobItems?.map((ji) => ({
              ...ji,
              provincialSalesTax: ji.jobItemTypeCode === LineItem.deductible ? 0 : ji.provincialSalesTax,
              gstHst: ji.jobItemTypeCode === LineItem.deductible ? 0 : ji.gstHst,
            }))
          : [],
        claimJobDocuments: it?.claimJobDocuments?.map((doc) => ({ ...doc, jobNumber: idx + 1 })),
      }));
      const claimJobDocuments = flatten(finalClaimJobs?.map((it) => it.claimJobDocuments) || []);

      const claimResponse = await claimSaveSimpleApi({
        ...data,
        contractId: data.contract?.id,
        contract: { id: data.contract?.id },
        statusCode:
          data.claimCreationOption === ClaimCreatorOptions.DEALERSHIP
            ? ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_DEALERSHIP
            : data.claimCreationOption === ClaimCreatorOptions.REPAIR_FACILITY
              ? ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_REPAIRFACILITY
              : ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_SELLING_DEALER,
        createdByPayee: {
          name: user?.fullName,
          email: user?.email,
          phoneNumber: undefined,
          payeeTypeId:
            data.claimCreationOption === ClaimCreatorOptions.DEALERSHIP
              ? PayeeType.DEALER_ID
              : data.claimCreationOption === ClaimCreatorOptions.REPAIR_FACILITY
                ? PayeeType.REPAIR_FACILITY_ID
                : undefined,
        },
        claimJobs: finalClaimJobs,
        contactName: data.contract?.client?.name,
        contactEmail: data.contract?.client?.email,
        contactPhone: data.contract?.client?.phone,
        streetAddress: data.contract?.client?.streetAddress,
        city: data.contract?.client?.city,
        postalCode: data.contract?.client?.postalCode,
        countryCode: data.contract?.client?.countryCode,
        provinceCode: data.contract?.client?.provinceCode,
        odometerReading: data.odometerReading ? data.odometerReading : 0,
      });

      const newClaimId = claimResponse.payload;
      if (claimResponse.status !== HttpStatus.ok || !newClaimId) {
        addLog({ error: claimResponse.message || lang.saveError });
        return;
      }
      const { payload: refetchedClaim } = await claimFindApi({ id: newClaimId });
      if (!refetchedClaim) {
        addLog({ error: lang.saveError });
        return;
      }
      if (refetchedClaim && claimResponse.status === HttpStatus.ok) {
        if (!isEmpty(data.claimDocuments)) {
          for (const document of data.claimDocuments) {
            document.claimId = refetchedClaim?.id;
            await claimDocumentSaveApi(document);
          }
        }

        if (!isEmpty(claimJobDocuments) && !isEmpty(refetchedClaim?.claimJobs)) {
          for (const claimJobDoc of claimJobDocuments) {
            const claimJob = refetchedClaim?.claimJobs?.find((it) => it?.jobNumber === claimJobDoc?.jobNumber);
            if (claimJobDoc && claimJob) await claimJobDocumentSaveApi({ ...claimJobDoc, claimJobId: claimJob.id });
          }
        }
      }

      addLog({ success: claimResponse.message || lang.saveSuccess });
      form.reload();
      (props.onSaved ?? props.onClose)();
    },
    validation: (data, errors) => {
      if (data.claimCreationOption === ClaimCreatorOptions.DEALERSHIP && !data?.dealerId)
        set(errors, 'dealerId', lang.required);
      if (data.claimCreationOption === ClaimCreatorOptions.REPAIR_FACILITY && !data?.repairFacilityId)
        set(errors, 'repairFacilityId', lang.required);

      for (const claimJob of data?.claimJobs ?? []) {
        const hasNoDocuments = data?.uploadableClaimTypeDocuments?.some(
          (ud) =>
            ud?.required &&
            ud?.claimTypeId === claimJob?.claimTypeId &&
            !claimJob?.claimJobDocuments?.some((cjd) => cjd.claimTypeDocumentId === ud?.id)
        );

        const hasMissingDataPoints = getVisibleDataPoints(claimJob?.dataPoints).some(
          (it) => it.type !== ClaimTypeDataPointInputType.Boolean && it.isRequired && !it.value
        );

        const hasInvalidItems =
          !claimJob?.claimTypeId ||
          claimJob?.claimJobItems?.some((item) =>
            item.jobItemTypeCode !== LineItem.deductible
              ? !item.lossCode || (item.gstHst ?? 0) > 1 || (item.provincialSalesTax ?? 0) > 1
              : (item.gstHst ?? 0) > 1 || (item.provincialSalesTax ?? 0) > 1
          );

        if (hasNoDocuments || hasMissingDataPoints || hasInvalidItems) {
          set(errors, `claimJobs._objectError`, lang.required);
        }
      }

      if (!data.claimDocuments) set(errors, 'claimDocuments._objectError', lang.required);
      if (!data.contract) set(errors, 'contract._objectError', lang.required);
    },
  });

  const repairFacilityListApi = useApi({
    action: Payee.list,
    default: { payeeTypeId: [PayeeType.REPAIR_FACILITY_ID], orderBy: 'name', orderType: 'asc' },
    wait: form.data.claimCreationOption === ClaimCreatorOptions.REPAIR_FACILITY,
  });
  const dealerListApi = useApi({
    action: Dealer.list,
    default: {
      id: user?.dealers?.map((d) => d.id) ?? undefined,
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      orderBy: 'name',
      orderType: 'asc',
    },
    wait: form.data.claimCreationOption === ClaimCreatorOptions.REPAIR_FACILITY,
  });
  const selectedDealer = useMemo(
    () => dealerListApi?.payload?.data?.find((it) => it.id === form.data.dealerId),
    [dealerListApi?.payload?.data, form.data.dealerId]
  );

  //#region Claim Types
  const claimTypesApi = useApi({
    action: ClaimType.list,
    body: {
      productId: [props.contract.productId],
      //dealerId: user?.dealers?.map((it) => it.id) ?? []
    },
    wait: !props.contract,
  });

  const claimTypes = useMemo(() => claimTypesApi?.payload?.data, [claimTypesApi?.payload?.data]);

  useEffect(() => {
    if (props.contract.productId)
      claimTypesApi.execute({
        productId: [props.contract.productId],
        //dealerId: selectedDealer?.id ? [selectedDealer?.id] : undefined,
      });
  }, [props.contract.productId]);

  const uploadableDocuments = useMemo(() => {
    const docs = flatten(claimTypes?.map((it) => it.claimTypeDocuments)) ?? [];
    form.update({ uploadableClaimTypeDocuments: docs });
    return docs;
  }, [claimTypes]);
  //#endregion

  //#region Insurers
  const insurerListApi = useApi({
    action: Insurer.list,
    body: {
      productId: [props.contract.productId],
    },
    wait: !props.contract,
  });
  useEffect(() => {
    if (props.contract.productId)
      insurerListApi.execute({
        productId: [props.contract.productId],
      });
  }, [props.contract.productId]);
  //#endregion

  //#region Claim job
  const [isEditingTax, setIsEditingTax] = useState<boolean>(false);
  useEffect(() => {
    if (isEditingTax) addLog({ warning: lang.claimJobItemTaxEditWarning });
  }, [isEditingTax]);

  function updateClaimJob(claimJob: Utils.FormData<Model.IClaimJob>, claimJobIndex: number) {
    const updatedClaimJobs = [...(form.data.claimJobs || [])];
    updatedClaimJobs[claimJobIndex] = { ...claimJob };
    form.merge({ claimJobs: updatedClaimJobs });
  }

  const isWarrantyProduct = form.data.contract?.product?.productType === ProductType.warrantiClassBased;
  const updateClaimJobClaimType = (updatedJob: Utils.FormData<Model.IClaimJob>, claimJobIndex: number) => {
    const currentClaimTypeId = updatedJob?.claimTypeId;
    const previousClaimTypeId = form.data?.claimJobs[claimJobIndex]?.claimTypeId;

    if (previousClaimTypeId === currentClaimTypeId) return;

    const claimJobs = [...(form.data.claimJobs ?? [])];
    const claimType = claimTypesApi?.payload?.data?.find((ct) => ct.id === currentClaimTypeId);
    const isDataPointVisible = createClaimTypeDataPointVisibility(claimType?.claimTypeAutomationRules);

    const newJob: Utils.FormData<Model.IClaimJob> = {
      ...updatedJob,
      claimTypeVersion: claimType?.version,
      dataPoints: sortBy(
        claimType?.claimTypeDataPoints?.map((dp) => ({
          claimTypeDataPointId: dp.id,
          claimTypeId: dp.claimTypeId,
          name: dp.name,
          type: dp.type,
          version: dp.version,
          isRequired: dp.isRequired,
          isVisible: isDataPointVisible(dp.name),
          value: undefined,
        })) ?? [],
        (it) => it.claimTypeDataPointId
      ),
      claimJobItems: [],
      claimJobDocuments: [],
    };

    if (ClaimCreatorOptions.DEALERSHIP === form.data.claimCreationOption && form.data?.dealerId) {
      const currency = Currency.CAD;
      const insurerAdjudications = Insurer.createInsurerAdjudications(insurerListApi?.payload?.data, currency);
      const labourRate = selectedDealer?.labourRate ?? form.data?.dealer?.labourRate;
      const newClaimJobItemPayee = {
        dealer: selectedDealer ?? form.data?.dealer,
        dealerId: selectedDealer?.id ?? form.data?.dealerId,
      };
      const paymentMethodCode = selectedDealer?.paymentMethodCode ?? form.data?.dealer?.paymentMethodCode;
      const tax = claimType?.products
        ?.find((it) => it.id === form.data?.contract?.productId)
        ?.productTaxes?.find((it) => it.provinceCode === selectedDealer?.provinceCode);

      if (!isEmpty(claimType?.claimTypeJobItemTemplates)) {
        newJob.claimJobItems =
          claimType?.claimTypeJobItemTemplates?.map((it) => {
            const templateLossCodes = filterLossCodesBySection(
              filterOutDisabledLossCodes(filterOutInternalLossCodes(it.lossCodes, !isDealer)),
              it.jobItemTypeCode
            );

            return {
              templateId: it.id,
              lossCodes: templateLossCodes,
              claimJobItemPayee: new ClaimJobItemPayee(newClaimJobItemPayee),
              jobItemTypeCode: it.jobItemTypeCode,
              adjudication: it.correction,
              lossCode: templateLossCodes.length === 1 ? first(templateLossCodes)?.code : undefined,
              gstHst: tax?.claimsGstHst || 0.0,
              provincialSalesTax: tax?.claimsProvincialSalesTax || 0.0,
              total: it.correction + it.correction * ((tax?.claimsGstHst ?? 0) + (tax?.claimsProvincialSalesTax ?? 0)),
              statusCode: PaymentStatus.unpaid,
              baseCost: 0,
              quantity: templateLossCodes?.length === 1 && it.jobItemTypeCode === LineItem.part ? 1 : 0,
              hoursWorked: 0,
              correction: it.correction,
              labourRate: it.jobItemTypeCode === LineItem.labour ? labourRate : 0.0,
              paymentMethodCode: paymentMethodCode,
              isCreatedFromTemplate: true,
              insurerAdjudications: insurerAdjudications,
              currency: currency,
            };
          }) || [];

        if (isWarrantyProduct) {
          const deductibleCorrection = form.data.contract?.deductible ?? 0.0;
          newJob.claimJobItems.push({
            claimJobItemPayee: new ClaimJobItemPayee(newClaimJobItemPayee),
            jobItemTypeCode: LineItem.deductible,
            adjudication: deductibleCorrection,
            gstHst: 0.0,
            provincialSalesTax: 0.0,
            total: deductibleCorrection,
            statusCode: PaymentStatus.unpaid,
            correction: deductibleCorrection,
            paymentMethodCode: paymentMethodCode,
            isCreatedFromTemplate: true,
            insurerAdjudications: insurerAdjudications,
            currency: currency,
          } as SimpleClaimJobItem);
        }
      }

      if (claimType?.allowManualJobItemsCreation) {
        const createJobItem = (
          jobItemTypeCode: LineItem,
          correction: number,
          insurerAdjudications: Model.IInsurerAdjudication
        ) => {
          const lossCodes = filterLossCodesBySection(
            filterOutDisabledLossCodes(filterOutInternalLossCodes(claimType?.lossCodes ?? [], !isDealer)),
            jobItemTypeCode
          );
          return {
            claimJobItemPayee: new ClaimJobItemPayee(newClaimJobItemPayee),
            jobItemTypeCode,
            statusCode: PaymentStatus.unpaid,
            currency: Currency.CAD,
            paymentMethodCode: paymentMethodCode,
            insurerAdjudications: insurerAdjudications,
            adjudication: jobItemTypeCode === LineItem.deductible ? correction : 0.0,
            correction: correction,
            gstHst: tax?.claimsGstHst,
            provincialSalesTax: tax?.claimsProvincialSalesTax,
            total:
              jobItemTypeCode === LineItem.deductible
                ? correction
                : correction + correction * ((tax?.claimsGstHst ?? 0) + (tax?.claimsProvincialSalesTax ?? 0)),
            baseCost: 0,
            quantity: jobItemTypeCode === LineItem.part ? 1 : 0,
            hoursWorked: jobItemTypeCode === LineItem.labour ? 1 : 0,
            labourRate: jobItemTypeCode === LineItem.labour ? labourRate : 0.0,
            isCreatedFromTemplate: false,
            lossCodes,
            lossCode: lossCodes.length === 1 ? first(lossCodes)?.code : undefined,
          } as SimpleClaimJobItem;
        };

        if (isWarrantyProduct) {
          newJob.claimJobItems.push(
            createJobItem(LineItem.deductible, form.data?.contract?.deductible, insurerAdjudications)
          );
        }

        newJob.claimJobItems.push(createJobItem(LineItem.labour, 0.0, insurerAdjudications));
        newJob.claimJobItems.push(createJobItem(LineItem.part, 0.0, insurerAdjudications));
        newJob.claimJobItems.push(createJobItem(LineItem.other, 0.0, insurerAdjudications));
      }
    }

    claimJobs[claimJobIndex] = newJob;
    form.update({ ...form.data, claimJobs });
  };
  const updateClaimJobDataPointValue = (value: string, id: number, claimJobIndex: number) => {
    const claimJob = form.data?.claimJobs?.[claimJobIndex];
    const updatedDataPoints = claimJob?.dataPoints?.map((item) =>
      item.claimTypeDataPointId === id ? { ...item, value } : item
    );
    updateClaimJob({ ...claimJob, dataPoints: updatedDataPoints }, claimJobIndex);
  };

  function deleteClaimJobItem(claimJobIndex: number, typeCode: string, itemIndex: number) {
    const claimJobs = [...(form.data.claimJobs ?? [])];
    const claimJob = claimJobs[claimJobIndex];

    if (!claimJob) return;

    const groupedClaimJobItems = groupBy(claimJob.claimJobItems || [], (it) => it.jobItemTypeCode);

    const targetItems = groupedClaimJobItems[typeCode] || [];
    targetItems.splice(itemIndex, 1);

    claimJob.claimJobItems = Object.values(groupedClaimJobItems).flat();
    claimJobs[claimJobIndex] = claimJob;

    form.update({
      ...form.data,
      claimJobs,
    });
  }
  function addClaimJobItem(claimJobIndex: number, jobItemTypeCode: string) {
    const claimJobs = [...(form.data.claimJobs ?? [])];
    const claimJob = claimJobs[claimJobIndex];

    if (!claimJob) return;

    const claimType = claimTypesApi?.payload?.data?.find((ct) => ct.id === claimJob?.claimTypeId);
    const currency = Currency.CAD;
    const insurerAdjudications = Insurer.createInsurerAdjudications(insurerListApi?.payload?.data, currency);
    const tax = claimType?.products
      ?.find((it) => it.id === form.data?.contract?.productId)
      ?.productTaxes?.find((it) => it.provinceCode === selectedDealer?.provinceCode);
    const labourRate = selectedDealer?.labourRate ?? form.data?.dealer?.labourRate;
    const newClaimJobItemPayee = {
      dealer: selectedDealer ?? form.data?.dealer,
      dealerId: selectedDealer?.id ?? form.data?.dealerId,
    };
    const paymentMethodCode = selectedDealer?.paymentMethodCode ?? form.data?.dealer?.paymentMethodCode;
    const lossCodes = filterLossCodesBySection(
      filterOutDisabledLossCodes(filterOutInternalLossCodes(claimType?.lossCodes ?? [], !isDealer)),
      jobItemTypeCode
    );

    const newItem = {
      claimJobItemPayee: new ClaimJobItemPayee(newClaimJobItemPayee),
      jobItemTypeCode,
      insurerAdjudications,
      lossCodes,
      lossCode: lossCodes.length === 1 ? first(lossCodes)?.code : undefined,
      currency,
      gstHst: tax?.claimsGstHst || 0.0,
      provincialSalesTax: tax?.claimsProvincialSalesTax || 0.0,
      correction: 0,
      adjudication: 0,
      statusCode: PaymentStatus.unpaid,
      baseCost: 0,
      quantity: jobItemTypeCode === LineItem.part ? 1 : 0,
      hoursWorked: jobItemTypeCode === LineItem.labour ? 1 : 0,
      labourRate: jobItemTypeCode === LineItem.labour ? labourRate : 0.0,
      paymentMethodCode,
      isCreatedFromTemplate: false,
    };

    if (jobItemTypeCode === LineItem.deductible) {
      newItem.gstHst = 0.0;
      newItem.provincialSalesTax = 0.0;
    }

    claimJob.claimJobItems = [...(claimJob.claimJobItems ?? []), newItem];
    claimJobs[claimJobIndex] = claimJob;

    form.update({
      ...form.data,
      claimJobs,
    });
  }
  const updateClaimJobItem = (
    fieldName: keyof Model.IClaimJobItem,
    value: any,
    targetItem: SimpleClaimJobItem,
    claimJobIndex: number
  ) => {
    const claimJob = form.data?.claimJobs?.[claimJobIndex];

    claimJob.claimJobItems = claimJob?.claimJobItems?.map((item) => {
      if (item !== targetItem) return item;

      const corr = fieldName === 'correction' ? value : (item.correction ?? 0.0);
      const gst = fieldName === 'gstHst' ? value : (item.gstHst ?? 0.0);
      const pst = fieldName === 'provincialSalesTax' ? value : (item.provincialSalesTax ?? 0.0);

      let updatedItem: SimpleClaimJobItem = {
        ...item,
        [fieldName]: value,
        correction: corr,
        adjudication: corr,
        total: Number((corr + corr * (gst + pst)).toFixed(2)),
      };

      if (fieldName === 'lossCode' && updatedItem.jobItemTypeCode === LineItem.part)
        updatedItem.quantity = !updatedItem.quantity ? 1 : updatedItem.quantity;

      if (updatedItem.jobItemTypeCode === LineItem.deductible) {
        updatedItem.gstHst = 0.0;
        updatedItem.provincialSalesTax = 0.0;
        updatedItem.total = corr;
      }

      updatedItem.hoursWorked =
        updatedItem.jobItemTypeCode === LineItem.labour && !item.hoursWorked ? 1 : item.hoursWorked;

      if (updatedItem.jobItemTypeCode === LineItem.part && (fieldName === 'baseCost' || fieldName === 'quantity')) {
        const quantity = Number(updatedItem.quantity ?? 0);
        const baseCost = Number(updatedItem.baseCost ?? 0);

        if (quantity === 0) {
          updatedItem = {
            ...updatedItem,
            quantity: 0,
            baseCost: 0,
            correction: 0,
            adjudication: 0,
            total: 0,
          };
        } else {
          const correction = baseCost * quantity;

          updatedItem = {
            ...updatedItem,
            quantity,
            baseCost,
            correction,
            adjudication: correction,
            total: Number((correction + correction * (gst + pst)).toFixed(2)),
          };
        }
      }

      return updatedItem;
    });

    updateClaimJob(claimJob, claimJobIndex);
  };

  function addNewClaimJob() {
    form.merge({
      claimJobs: [
        ...(form.data.claimJobs ?? []),
        {
          claimTypeId: null,
          cause: undefined,
          customerComplaint: undefined,
          correction: undefined,
          claimJobDocuments: [],
          dataPoints: [],
          claimJobItems: [],
          statusCode: ClaimJobStatusCode.OPEN_ACTIVE_IN_PROGRESS,
        },
      ],
    });
  }
  const deleteClaimJob = (claimJobIndex: number) => {
    const updatedClaimJobs = form.data?.claimJobs?.filter((_, idx) => idx !== claimJobIndex);
    form.update({ claimJobs: updatedClaimJobs });
  };
  //#endregion
  const shouldShowClaimJobs = (() => {
    if (!form.data.claimCreationOption) return false;
    if (form.data.claimCreationOption === ClaimCreatorOptions.REPAIR_FACILITY)
      return !!(form.data.repairFacilityId || form.data.repairFacility);
    if (form.data.claimCreationOption === ClaimCreatorOptions.DEALERSHIP)
      return !!(form.data.dealerId || form.data.dealer);
    return true;
  })();
  const resetClaimData = (claim: Partial<SimpleClaimForm>) => {
    if (!claim) return undefined;

    claim.claimJobs = !claim.claimJobs
      ? []
      : claim.claimJobs.map((job) => ({
          ...job,
          claimTypeId: undefined,
          customerComplaint: undefined,
          cause: undefined,
          correction: undefined,
          claimJobItems: [],
          claimJobDocuments: [],
          dataPoints: [],
        }));
    claim.odometerReading = undefined;
    claim.workOrderNumber = undefined;

    return claim;
  };

  return {
    form,
    claimTypes,
    uploadableDocuments,
    dealerListApi,
    selectedDealer,
    isWarrantyProduct,
    shouldShowClaimJobs,
    repairFacilityListApi,
    isEditingTax,
    setIsEditingTax,
    resetClaimData,
    addNewClaimJob,
    deleteClaimJob,
    updateClaimJob,
    addClaimJobItem,
    updateClaimJobItem,
    deleteClaimJobItem,
    updateClaimJobDataPointValue,
    updateClaimJobClaimType,
  };
}
